import { useState } from 'react';
import { PiMagicWandDuotone } from "react-icons/pi";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { signUp, signIn } from '@/utils/auth';
import { useResilientAction } from '@/hooks/useResilientAction';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { User, Phone, Lock, Loader2 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export function Register() {
  const { t, currentLang } = useI18n();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError(t('error.password_mismatch'));
      return false;
    }
    if (formData.password.length < 6) {
      setError(t('error.min_length', { count: 6 }));
      return false;
    }
    if (!acceptTerms) {
      setError(currentLang === 'bn' ? 'শর্তাবলী মেনে নিন' : 'Please accept terms');
      return false;
    }
    return true;
  };

  const { isLoading, execute: resilientSignUp } = useResilientAction(
    async (signal: AbortSignal) => {
      void signal;
      // Create the user. The DB trigger will auto-confirm the email instantly.
      await signUp(formData.phone, formData.password, formData.fullName, formData.phone);
      
      // Auto-login since the account is confirmed
      await signIn(formData.phone, formData.password);
      return true;
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    try {
      const result = await resilientSignUp();
      
      if (result) {
        toast.success(currentLang === 'bn' ? 'রেজিস্ট্রেশন এবং লগইন সফল হয়েছে!' : 'Registered and logged in successfully!');
        navigate('/');
      }
    } catch (err: unknown) {
      const errObj = err as Record<string, unknown>;
      const message = typeof errObj?.message === 'string' ? errObj.message : String(err);
      setError(
        message ||
        (currentLang === 'bn'
          ? 'রেজিস্ট্রেশন ব্যর্থ হয়েছে'
          : 'Registration failed')
      );
    }
  };

  const generatePassword = (difficulty: 'easy' | 'medium' | 'hard') => {
    const lowers = 'abcdefghijklmnopqrstuvwxyz';
    const uppers = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*';
    const extraSymbols = '-_+?';

    let chars: string;
    let length: number;

    if (difficulty === 'easy') {
      chars = lowers + uppers + numbers;
      length = 8;
    } else if (difficulty === 'medium') {
      chars = lowers + uppers + numbers + symbols;
      length = 12;
    } else {
      chars = lowers + uppers + numbers + symbols + extraSymbols;
      length = 16;
    }

    const randomValues = new Uint32Array(length);
    crypto.getRandomValues(randomValues);

    const newPassword = Array.from(randomValues, (v) => chars[v % chars.length]).join('');

    setFormData(prev => ({ ...prev, password: newPassword, confirmPassword: newPassword }));
    setShowPassword(true);
    toast.success(`Generated a ${difficulty} password!`);
  };

  return (
    <div className="min-h-[calc(100dvh-4rem)] flex items-center justify-center bg-white py-10 px-4">
      <div className="w-full max-w-md mx-auto">
        <Card className="rounded-none shadow-[0px_3px_6px_rgba(0,0,0,0.2)]">
          <CardHeader className="text-center">
            <div className="mx-auto -m-8 w-[200px] h-[200px] rounded-none overflow-hidden flex items-center justify-center">
              <img src="/images/logo-png.png" alt="App Icon"/>
            </div>
            <CardTitle className="text-2xl">{t('register.title')}</CardTitle>
            <CardDescription className='text-black'>{t('register.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="fullName">{t('form.full_name')} *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                  <Input
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="pl-10"
                    placeholder={t('form.full_name')}
                  />
                </div>
              </div>



              <div className="space-y-2">
                <Label htmlFor="phone">{t('form.phone')} *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="pl-10"
                    placeholder="01XXXXXXXXX"
                  />
                </div>
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">{t('form.password')} *</Label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="inline-flex items-center gap-1 text-xs font-medium outline-none focus-visible:ring-0" style={{ color: 'var(--brand-primary)' }}>
                          <PiMagicWandDuotone className="w-4 h-4 text-primary" />
                          <span className="text-primary">{t('autogen.title')}</span>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" side="bottom" sideOffset={0} className="w-auto min-w-[105px] z-[60]">
                        <DropdownMenuItem onClick={() => generatePassword('easy')} className="cursor-pointer outline-none focus:bg-tb-primary-light focus:text-black">{t('autogen.easy')}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => generatePassword('medium')} className="cursor-pointer outline-none focus:bg-tb-primary-light focus:text-black">{t('autogen.medium')}</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => generatePassword('hard')} className="cursor-pointer outline-none focus:bg-tb-primary-light focus:text-black">{t('autogen.hard')}</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      minLength={6}
                      className="pl-10 pr-10"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-700 hover:text-gray-700"
                    >
                      {showPassword ? (
                        <FaEyeSlash className="h-4 w-4" />
                      ) : (
                        <FaEye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('form.confirm_password')} *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                      className="pl-10"
                      placeholder="••••••••"
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="terms"
                  checked={acceptTerms}
                  onCheckedChange={(checked: boolean | 'indeterminate') =>
                    setAcceptTerms(checked === true)
                  }
                />
                <Label htmlFor="terms" className="text-sm leading-tight">
                  {t('register.terms')}
                </Label>
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {currentLang === 'bn' ? 'রেজিস্টার হচ্ছে...' : 'Registering...'}
                  </>
                ) : (
                  t('nav.register')
                )}
              </Button>
            </form>

            <p className="text-center text-sm mt-6">
              {t('register.has_account')}{' '}
              <Link to="/login" className="text-primary hover:underline font-medium">
                {t('register.login')}
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
